<template>
  <div id="entity_scoring" class="flex flex-col gap-10">
    <div class="flex flex-col gap-6">
      <span class="text_color text-xl font-semibold">Entity Scoring</span>
      <span class="text_color text-sm font-normal"
        >Use this feature to define custom rules that automatically score Leads,
        Contacts, and Companies based on behavior, demographics, and engagement.
        Scoring helps prioritize follow-ups and focus on high-value
        opportunities.</span
      >
    </div>
    <div class="flex flex-col">
      <SkeletonLoader v-if="isLoading" :page="loaderPages.ENTITY_SCORING" />
      <CustomTable
        v-else-if="entities?.length > 0"
        empty-cell-value="--"
        :headers="entityScoringHeaders"
        :data="entities"
        :pagination="pagination"
        :custom-column-render="customColumnRender"
        @update:pagination="handlePageChange"
        :custom-row="customRow"
        identifier-key="entity_type"
      />
      <EmptyScreen v-else source="entity_scoring" />
    </div>
  </div>
</template>

<script setup>
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import { onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { entityScoringHeaders } from "@/src/constants/entityScoringConstants.js";
import EntityScoringToggle from "@/src/views/entity_scoring/EntityScoringToggle.vue";
import { useRouter } from "vue-router";
import DashboardService from "@/src/api/DashboardService.js";

const router = useRouter();

const isLoading = ref(false);
const entities = ref([]);
const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
});

const customColumnRender = {
  entity_status: {
    component: EntityScoringToggle,
    props: (record) => ({
      row: record,
    }),
  },
};

const getEntities = async () => {
  try {
    isLoading.value = true;

    const response = await DashboardService.fetchScoringEntities();

    if (response?.success) {
      entities.value = response?.entities || [];
    } else {
      message.error("Failed to fetch entities");
    }
  } catch (error) {
    message.error("An error occurred while fetching entities");
    console.error("An error occurred while fetching entities: ", error);
  } finally {
    isLoading.value = false;
  }
};

const customRow = (record) => {
  const resolvedRoute = router.resolve({
    name: "entity-scoring",
    params: { id: record.entity_type },
  });

  return {
    onClick: (e) => {
      if (e.target.closest("[data-no-row-click]")) return;
      router.push(resolvedRoute);
    },
    "data-href": resolvedRoute.href,
  };
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
};

onMounted(() => {
  getEntities();
});
</script>
